import { useState } from 'react'
import { Palmtree, Shapes, Tag, BookOpen, User, Settings2, Droplets, Heart, LogOut, LogIn, Bell, MessageCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useSession, useInvalidateSession } from '@/hooks/use-session'
import { useLocaleNavigate } from '@/hooks/use-locale-navigate'
import { useNotify } from '@/hooks/use-notify'
import { useDropsBalance } from '@/hooks/use-drops'
import { useMessages, useNotifications } from '@swim/ui'
import { swimApi } from '@swim/api-client'
import { LocaleLink } from '@/components/LocaleLink'
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerDescription,
} from '@/components/ui/drawer'

const DOCS_URL = (import.meta as ImportMeta & { env: Record<string, string> }).env.VITE_DOCS_URL ?? 'https://docs.swimflows.com'

// Matches SideNav.tsx's (desktop) always-visible rail exactly: Home,
// Categories, Claimed Offers. 'Streams' and 'Browse' are deliberately left
// out — desktop hides Streams (not ready yet, see SideNav.tsx NAV_ITEMS
// hidden:true) and Browse is command-palette-only, never a nav destination.
const NAV_ITEMS = [
  { key: 'home',       label: 'Home',           route: '/',                       Icon: Palmtree, external: false },
  { key: 'categories', label: 'Categories',      route: '/categories',            Icon: Shapes,   external: false },
  { key: 'claimed',    label: 'Claimed Offers',  route: '/account/claimed-offers', Icon: Tag,      external: false },
  { key: 'docs',       label: 'Docs',            route: DOCS_URL,                 Icon: BookOpen, external: true  },
] as const

interface MobileNavDrawerProps {
  open: boolean
  onClose: () => void
}

export function MobileNavDrawer({ open, onClose }: MobileNavDrawerProps) {
  const [imageLoadFailed, setImageLoadFailed] = useState(false)
  const { user, isLoading } = useSession()
  const { navigate } = useLocaleNavigate()
  const invalidateSession = useInvalidateSession()
  const notify = useNotify()
  const { t: tNav } = useTranslation('navigation')
  const { t } = useTranslation('common')
  const { data: dropsData } = useDropsBalance()
  const dropsBalance = dropsData?.balance ?? 0

  const { unreadCount: unreadNotifCount } = useNotifications({
    apiBase: (import.meta as ImportMeta & { env: Record<string, string> }).env.VITE_API_URL ?? '',
    getAuthHeader: () => null, // Storefront uses cookie auth
    enabled: !!user,
    sseEnabled: false, // SSE not built yet (Web-1kr2)
  })

  const { unreadThreadCount } = useMessages({
    apiBase: (import.meta as ImportMeta & { env: Record<string, string> }).env.VITE_API_URL ?? '',
    getAuthHeader: () => null, // Storefront uses cookie auth
    enabled: false, // was: enabled: !!user — messages polling killed until the feature is built, see Web-z8o8
  })

  const initial = user?.name?.charAt(0).toUpperCase()
    ?? user?.email?.charAt(0).toUpperCase()
    ?? 'U'

  function go(route: string) {
    onClose()
    navigate(route)
  }

  async function handleSignOut() {
    onClose()
    const result = await swimApi.post('/auth/customer/sign-out', {})
    if (!result.success) {
      console.error('[MobileNavDrawer] sign-out failed:', result.error)
      notify.error(t('toast.signOutError'))
      return
    }
    await invalidateSession()
    notify.success(t('chrome.avatar.toastSignedOut'))
    navigate('/')
  }

  const tileBase = `
    flex flex-col items-start gap-1.5
    px-4 py-3.5 rounded-xl
    bg-slate-700/50 dark:bg-slate-800/60
    hover:bg-slate-700 dark:hover:bg-slate-800
    transition-colors text-left w-full
  `

  return (
    <Drawer open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <DrawerContent className="
        max-h-[85vh] flex flex-col
        bg-slate-800 dark:bg-slate-900
        border-slate-700 dark:border-slate-700
        rounded-t-2xl
      ">
        <DrawerTitle className="sr-only">{t('mobile.menu')}</DrawerTitle>
        <DrawerDescription className="sr-only">{t('mobile.menu')}</DrawerDescription>

        {/* ── Auth header ─────────────────────────────────────────────────── */}
        <div className="flex items-center gap-3 px-5 pt-1 pb-4 flex-shrink-0">
          {isLoading ? (
            /* Session loading — shimmer placeholder */
            <>
              <div className="w-10 h-10 rounded-full bg-sand-200 dark:bg-slate-700 animate-pulse flex-shrink-0" />
              <div className="flex flex-col gap-1.5 min-w-0">
                <div className="h-3 w-24 rounded bg-sand-200 dark:bg-slate-700 animate-pulse" />
                <div className="h-2 w-32 rounded bg-sand-200/60 dark:bg-slate-700/60 animate-pulse" />
              </div>
            </>
          ) : user ? (
            /* Authenticated — real avatar + name + email */
            <>
              <div className="
                w-10 h-10 rounded-full flex-shrink-0
                bg-sand-100 dark:bg-slate-700
                flex items-center justify-center overflow-hidden
                border-2 border-ocean-500/40
              ">
                {user.image && !imageLoadFailed ? (
                  <img
                    src={user.image}
                    alt={user.name ?? ''}
                    className="h-full w-full object-cover"
                    onError={() => setImageLoadFailed(true)}
                  />
                ) : (
                  <span className="text-sm font-medium text-ocean-500 dark:text-ocean-300 font-sans">
                    {initial}
                  </span>
                )}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white font-sans truncate leading-tight">
                  {user.name ?? user.email}
                </p>
                {user.email && user.name && (
                  <p className="text-[10px] font-mono tracking-wide text-slate-400 truncate mt-0.5">
                    {user.email}
                  </p>
                )}
              </div>
            </>
          ) : (
            /* Guest — generic icon + label */
            <>
              <div className="
                w-10 h-10 rounded-full flex-shrink-0
                bg-slate-700
                flex items-center justify-center
                border-2 border-slate-600
              ">
                <User className="w-5 h-5 text-slate-400" aria-hidden />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-300 font-sans">{t('mobile.menu')}</p>
              </div>
            </>
          )}
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-4 pb-4">

          {/* ── Navigate ───────────────────────────────────────────────── */}
          <nav aria-label={tNav('chrome.mobile.navigate')}>
            <p className="text-[9px] font-mono tracking-widest uppercase text-slate-500 mb-2 px-1">
              {tNav('chrome.mobile.navigate')}
            </p>

            <div className="grid grid-cols-2 gap-2 mb-4">
              {/* Claimed Offers only when signed in — global rule (Mars 2026-07-05) */}
              {NAV_ITEMS.filter(({ key }) => key !== 'claimed' || !!user).map(({ key, label, route, Icon, external }) =>
                external ? (
                  <a
                    key={key}
                    href={route}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={onClose}
                    className={tileBase}
                  >
                    <Icon className="w-4 h-4 text-slate-400" aria-hidden />
                    <span className="text-sm font-medium font-sans text-white">{label}</span>
                  </a>
                ) : (
                  <button key={key} onClick={() => go(route)} className={tileBase}>
                    <Icon className="w-4 h-4 text-slate-400" aria-hidden />
                    <span className="text-sm font-medium font-sans text-white">{label}</span>
                  </button>
                )
              )}
            </div>
          </nav>

          {/* ── Account (authenticated only) ───────────────────────────── */}
          {user ? (
            <>
              <div className="border-t border-slate-700 dark:border-slate-700/60 mb-3" />
              <nav aria-label={tNav('chrome.mobile.account')}>
                <p className="text-[9px] font-mono tracking-widest uppercase text-slate-500 mb-2 px-1">
                  {tNav('chrome.mobile.account')}
                </p>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <LocaleLink to="/profile" onClick={onClose} className={tileBase}>
                    <User className="w-4 h-4 text-slate-400" aria-hidden />
                    <span className="text-sm font-medium font-sans text-white">{t('mobile.profile')}</span>
                  </LocaleLink>
                  <LocaleLink to="/settings" onClick={onClose} className={tileBase}>
                    <Settings2 className="w-4 h-4 text-slate-400" aria-hidden />
                    <span className="text-sm font-medium font-sans text-white">{t('mobile.settings')}</span>
                  </LocaleLink>
                  {/* Workspaces + your Streams live in the logo drawer's workspace switcher, not here */}
                  <LocaleLink to="/messages" onClick={onClose} className={tileBase}>
                    <MessageCircle className="w-4 h-4 text-slate-400" aria-hidden />
                    <span className="text-sm font-medium font-sans text-white">{t('mobile.messages')}</span>
                    {unreadThreadCount > 0 && (
                      <span className="text-xs font-bold font-sans tabular-nums text-ocean-400 leading-none">
                        {unreadThreadCount}
                      </span>
                    )}
                  </LocaleLink>
                  <LocaleLink to="/notifications" onClick={onClose} className={tileBase}>
                    <Bell className="w-4 h-4 text-slate-400" aria-hidden />
                    <span className="text-sm font-medium font-sans text-white">{t('mobile.notifications')}</span>
                    {unreadNotifCount > 0 && (
                      <span className="text-xs font-bold font-sans tabular-nums text-ocean-400 leading-none">
                        {unreadNotifCount}
                      </span>
                    )}
                  </LocaleLink>
                  <LocaleLink to="/account/drops" onClick={onClose} className={tileBase}>
                    <Droplets className="w-4 h-4 text-ocean-400" aria-hidden />
                    <span className="text-sm font-medium font-sans text-white">{t('mobile.drops')}</span>
                    <span className="text-xs font-bold font-sans tabular-nums text-ocean-400 leading-none">
                      {dropsBalance.toLocaleString()} ◉
                    </span>
                  </LocaleLink>
                  <LocaleLink to="/account/saves" onClick={onClose} className={tileBase}>
                    <Heart className="w-4 h-4 text-slate-400" aria-hidden />
                    <span className="text-sm font-medium font-sans text-white">{t('mobile.savedOffers')}</span>
                  </LocaleLink>
                  {/* Claimed Offers intentionally NOT here — it lives in the NAVIGATE
                      grid above (signed-in only); duplicating it made 7 tiles (Mars 2026-07-05) */}
                </div>
              </nav>

              {/* Log out */}
              <div className="border-t border-slate-700 dark:border-slate-700/60 mb-3" />
              <button
                onClick={() => void handleSignOut()}
                className="
                  w-full flex items-center gap-3
                  px-4 py-3 rounded-xl
                  text-white text-sm font-medium font-sans text-left
                  hover:bg-slate-700/50 transition-colors
                "
              >
                <LogOut className="w-4 h-4 text-slate-400 flex-shrink-0" aria-hidden />
                {t('chrome.avatar.signOut')}
              </button>
            </>
          ) : (
            /* Guest — Log in row */
            <>
              <div className="border-t border-slate-700 dark:border-slate-700/60 mb-3" />
              <button
                onClick={() => { onClose(); navigate('/sign-in') }}
                className="
                  w-full flex items-center gap-3
                  px-4 py-3 rounded-xl
                  text-white text-sm font-medium font-sans text-left
                  hover:bg-slate-700/50 transition-colors
                "
              >
                <LogIn className="w-4 h-4 text-ocean-400 flex-shrink-0" aria-hidden />
                {t('chrome.avatar.logIn')}
              </button>
            </>
          )}
        </div>

        {/* Safe area padding for iPhone home indicator */}
        <div className="flex-shrink-0 h-safe-bottom pb-2" />
      </DrawerContent>
    </Drawer>
  )
}
